import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFragrances } from '../../contexts/FragranceContext';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';

const SearchAndFilter: React.FC = () => {
  const {
    filters,
    sorting,
    pageSize,
    setFilters,
    setSorting,
    setPageSize,
    clearFilters,
    fetchFragrances,
    getAutocompleteSuggestions,
    isLoading
  } = useFragrances();

  interface FragranceSorting {
    sort_by: string;
    sort_order: 'asc' | 'desc';
  }

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  const [localSorting, setLocalSorting] = useState<FragranceSorting>({
    sort_by: 'house',
    sort_order: 'asc', // TypeScript now knows this is "asc"
  });
  const [houseSuggestions, setHouseSuggestions] = useState<string[]>([]);
  const [showHouseSuggestions, setShowHouseSuggestions] = useState(false);
  const [notesSuggestions, setNotesSuggestions] = useState<string[]>([]);
  const [showNotesSuggestions, setShowNotesSuggestions] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const { token } = useAuth();

  // Debounced search
  const [searchQuery, setSearchQuery] = useState(filters.search || '');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const localFiltersRef = useRef(localFilters);
  localFiltersRef.current = localFilters;

  // Load house suggestions
  useEffect(() => {
    if (!token) return;
    const loadHouseSuggestions = async () => {
      const suggestions = await getAutocompleteSuggestions('house', localFilters.house || '');
      setHouseSuggestions(suggestions);
    };
    loadHouseSuggestions();
  }, [getAutocompleteSuggestions, localFilters.house, token]);

  // Load notes suggestions
  useEffect(() => {
    if (!token) return;
    const loadNotesSuggestions = async () => {
      if (localFilters.notes && localFilters.notes.length > 1) {
        const suggestions = await getAutocompleteSuggestions('note', localFilters.notes || '');
        setNotesSuggestions(suggestions);
      }
    };
    loadNotesSuggestions();
  }, [getAutocompleteSuggestions, localFilters.notes, token]);

  useEffect(() => {
    // Sync local state with context on mount
    setLocalSorting(sorting);
    setLocalFilters(filters);
  }, [sorting, filters]);

  useEffect(() => {
    if (token) {
      fetchFragrances(filters, sorting);
    }
  }, [token, fetchFragrances, filters, sorting]);
  

  // Handle search with debouncing
  useEffect(() => {
    if (!token) return;
  
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  
    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      const newFilters = { ...localFiltersRef.current, search: searchQuery || undefined };
  
      // Only update state if filters actually changed
      if (JSON.stringify(newFilters) !== JSON.stringify(localFiltersRef.current)) {
        setLocalFilters(newFilters);
        setFilters(newFilters);
        fetchFragrances(newFilters, localSorting);
      }
    }, 500);
  
    // Cleanup on unmount or before next effect run
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, token, fetchFragrances, localSorting, setFilters]); // Only depend on searchQuery to prevent infinite loops

  const handleFilterChange = useCallback((key: string, value: any) => {
    const newFilters = { ...localFilters, [key]: value || undefined };
    setLocalFilters(newFilters);
  }, [localFilters]);

  const handleSortingChange = useCallback((key: string, value: any) => {
    let newSorting = { ...localSorting, [key]: value };
  
    if (key === 'sort_by') {
      // If user switches to a rating field, default to DESC
      if (['winter_rating','spring_rating','summer_rating','fall_rating','day_rating','night_rating','overall_rating','longevity','sillage']
        .includes(value)) {
        newSorting.sort_order = 'desc';
      } else {
        // Otherwise default ASC (name, house, etc.)
        newSorting.sort_order = 'asc';
      }
    }
  
    setLocalSorting(newSorting);
    setSorting(newSorting);
    fetchFragrances(localFilters, newSorting);
  }, [localSorting, localFilters, setSorting, fetchFragrances]);

  const applyFilters = () => {
    setFilters(localFilters);
    fetchFragrances(localFilters, localSorting);
    setIsFilterOpen(false);
  };

  const resetFilters = () => {
    const emptyFilters = {};
    setLocalFilters(emptyFilters);
    setSearchQuery('');
    clearFilters();
    fetchFragrances(emptyFilters, localSorting);
    setIsFilterOpen(false);
  };

  const handleHouseSelect = (house: string) => {
    handleFilterChange('house', house);
    setShowHouseSuggestions(false);
  };

  const handleNotesSelect = (note: string) => {
    handleFilterChange('notes', note);
    setShowNotesSuggestions(false);
  };

  const loadSearchSuggestions = async (query: string) => {
    try {
      const [houseSuggestions, nameSuggestions] = await Promise.all([
        getAutocompleteSuggestions('house', query),
        getAutocompleteSuggestions('fragrance_name', query)
      ]);
      
      // Combine and deduplicate suggestions
      const allSuggestions = [...houseSuggestions, ...nameSuggestions];
      const uniqueSuggestions = allSuggestions.filter((item, index) => allSuggestions.indexOf(item) === index);
      setSearchSuggestions(uniqueSuggestions.slice(0, 10));
      setShowSearchSuggestions(true);
    } catch (error) {
      console.error('Error loading search suggestions:', error);
      setShowSearchSuggestions(false);
    }
  };

  const activeFilterCount = Object.values(localFilters).filter(v => v !== undefined && v !== null && v !== '').length;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
      {/* Search Bar */}
      <div className="flex flex-col gap-4">
        <div className="flex-1 relative">
          <Input
            type="text"
            placeholder="Search fragrances by name or house..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (e.target.value.length > 1) {
                loadSearchSuggestions(e.target.value);
              } else {
                setShowSearchSuggestions(false);
              }
            }}
            onFocus={() => {
              if (searchQuery.length > 1) {
                loadSearchSuggestions(searchQuery);
              }
            }}
            onBlur={() => setTimeout(() => setShowSearchSuggestions(false), 200)}
            className="w-full"
          />
          
          {/* Search Suggestions Dropdown */}
          {showSearchSuggestions && searchSuggestions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {searchSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSearchQuery(suggestion);
                    setShowSearchSuggestions(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-stretch sm:items-center">
          {/* Sort Dropdown */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:block">
              Sort by:
            </label>
            <select
              value={localSorting.sort_by}
              onChange={(e) => handleSortingChange('sort_by', e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 sm:px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 sm:flex-none"
            >
              <option value="house">House</option>
              <option value="name">Name</option>
              <option value="overall_rating">Overall Rating</option>
              <option value="longevity">Longevity</option>
              <option value="sillage">Sillage</option>
              <option value="winter_rating">Winter Rating</option>
              <option value="spring_rating">Spring Rating</option>
              <option value="summer_rating">Summer Rating</option>
              <option value="fall_rating">Fall Rating</option>
              <option value="day_rating">Day Rating</option>
              <option value="night_rating">Night Rating</option>
              <option value="updated_at">Last Modified</option>
              <option value="created_at">Date Added</option>
            </select>
            <button
              onClick={() => handleSortingChange('sort_order', localSorting.sort_order === 'asc' ? 'desc' : 'asc')}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200 bg-gray-100 dark:bg-gray-700 rounded-lg flex-shrink-0"
              title={`Currently: ${localSorting.sort_order === 'asc' ? 'Ascending' : 'Descending'} - Click to toggle`}
            >
              {localSorting.sort_order === 'asc' ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                </svg>
              )}
            </button>
          </div>

          {/* Page Size Selector */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:block">
              Per page:
            </label>
            <select
              value={pageSize}
              onChange={(e) => {
                const newSize = parseInt(e.target.value);
                setPageSize(newSize);
                fetchFragrances(filters, sorting, 1, newSize);
              }}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 sm:px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 sm:flex-none"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>

          {/* Filter Toggle */}
          <Button
            variant="outline"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="relative flex-shrink-0"
            size="sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
            {activeFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
                {/* House Filter */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    House
                  </label>
                  <input
                    type="text"
                    placeholder="Filter by house..."
                    value={localFilters.house || ''}
                    onChange={(e) => {
                      handleFilterChange('house', e.target.value);
                      setShowHouseSuggestions(e.target.value.length > 0);
                    }}
                    onFocus={() => setShowHouseSuggestions((localFilters.house || '').length > 0)}
                    onBlur={() => setTimeout(() => setShowHouseSuggestions(false), 200)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  
                  {/* House Suggestions Dropdown */}
                  {showHouseSuggestions && houseSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {houseSuggestions.map((house, index) => (
                        <button
                          key={index}
                          onClick={() => handleHouseSelect(house)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm"
                        >
                          {house}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Notes Filter */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes
                  </label>
                  <input
                    type="text"
                    placeholder="Filter by notes..."
                    value={localFilters.notes || ''}
                    onChange={(e) => {
                      handleFilterChange('notes', e.target.value);
                      setShowNotesSuggestions(e.target.value.length > 0);
                    }}
                    onFocus={() => setShowNotesSuggestions((localFilters.notes || '').length > 0)}
                    onBlur={() => setTimeout(() => setShowNotesSuggestions(false), 200)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  
                  {/* Notes Suggestions Dropdown */}
                  {showNotesSuggestions && notesSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {notesSuggestions.map((note, index) => (
                        <button
                          key={index}
                          onClick={() => handleNotesSelect(note)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm"
                        >
                          {note}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Concentration Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Concentration
                  </label>
                  <select
                    value={localFilters.concentration || ''}
                    onChange={(e) => handleFilterChange('concentration', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">All Concentrations</option>
                    <option value="EdC">Eau de Cologne</option>
                    <option value="EdT">Eau de Toilette</option>
                    <option value="EdP">Eau de Parfum</option>
                    <option value="Parfum">Parfum</option>
                    <option value="Elixir">Elixir</option>
                    <option value="Extrait">Extrait de Parfum</option>
                  </select>
                </div>

                {/* Rating Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Min Rating
                  </label>
                  <select
                    value={localFilters.min_rating || ''}
                    onChange={(e) => handleFilterChange('min_rating', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Any Rating</option>
                    {Array.from({ length: 10 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}+ Stars</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Max Rating
                  </label>
                  <select
                    value={localFilters.max_rating || ''}
                    onChange={(e) => handleFilterChange('max_rating', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Any Rating</option>
                    {Array.from({ length: 10 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1} Stars</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Toggle Filters */}
              <div className="flex flex-wrap gap-4 mb-6">
                <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                  <input
                    type="checkbox"
                    checked={localFilters.is_favorite === true}
                    onChange={(e) => handleFilterChange('is_favorite', e.target.checked ? true : undefined)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">❤️ Favorites only</span>
                </label>

                <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                  <input
                    type="checkbox"
                    checked={localFilters.is_decant === true}
                    onChange={(e) => handleFilterChange('is_decant', e.target.checked ? true : undefined)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">🧪 Decants only</span>
                </label>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 space-y-6">
                {/* Seasonality Filters */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Seasons (≥3 rating)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                      <input
                        type="checkbox"
                        checked={localFilters.winter_rating === true}
                        onChange={(e) => handleFilterChange('winter_rating', e.target.checked ? true : undefined)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">❄️ Winter (3+)</span>
                    </label>

                    <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                      <input
                        type="checkbox"
                        checked={localFilters.spring_rating === true}
                        onChange={(e) => handleFilterChange('spring_rating', e.target.checked ? true : undefined)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">🌸 Spring (3+)</span>
                    </label>

                    <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                      <input
                        type="checkbox"
                        checked={localFilters.summer_rating === true}
                        onChange={(e) => handleFilterChange('summer_rating', e.target.checked ? true : undefined)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">☀️ Summer (3+)</span>
                    </label>

                    <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                      <input
                        type="checkbox"
                        checked={localFilters.fall_rating === true}
                        onChange={(e) => handleFilterChange('fall_rating', e.target.checked ? true : undefined)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">🍂 Fall (3+)</span>
                    </label>
                  </div>
                </div>

                {/* Day/Night Filters */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Time of Day (≥3 rating)
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                      <input
                        type="checkbox"
                        checked={localFilters.day_rating === true}
                        onChange={(e) => handleFilterChange('day_rating', e.target.checked ? true : undefined)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">☀️ Day (3+)</span>
                    </label>

                    <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                      <input
                        type="checkbox"
                        checked={localFilters.night_rating === true}
                        onChange={(e) => handleFilterChange('night_rating', e.target.checked ? true : undefined)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">🌙 Night (3+)</span>
                    </label>
                  </div>
                </div>
              </div>
              {/* Filter Actions */}
              <div className="flex items-center space-x-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button onClick={applyFilters} disabled={isLoading}>
                  Apply Filters
                </Button>
                <Button variant="outline" onClick={resetFilters}>
                  Clear All
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchAndFilter;